import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useCurrentUser from 'hooks/useCurrentUser';
import { isAnyPermitted } from 'util/PermissionsMixin';
import useCollectionMutation from 'collections/hooks/useCollectionMutation';
import { ButtonToolbar, MenuItem, DeleteMenuItem } from 'components/bootstrap';
import { ConfirmDialog, IfPermitted, ShareButton } from 'components/common';
import { MoreActions } from 'components/common/EntityDataTable';
import Routes from 'routing/Routes';
import EntityShareModal from 'components/permissions/EntityShareModal';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';
import type { CollectionListItem } from 'collections/types';
import { COLLECTIONS_QUERY_KEY } from 'collections/hooks/useCollections';

type Props = {
  id: CollectionListItem['id'];
  title: CollectionListItem['title'];
};

const CollectionActions = ({ id, title }: Props) => {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const refetchCollections = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [COLLECTIONS_QUERY_KEY],
      }),
    [queryClient],
  );

  const { deselectEntity } = useSelectedEntities();
  const { deleteCollection } = useCollectionMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showCollectionShareModal, setShowCollectionShareModal] = useState<boolean>(false);
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState<boolean>(false);

  const toggleCollectionShareModal = () => setShowCollectionShareModal(!showCollectionShareModal);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);

  const handleShareClose = (type: 'collection' | 'collectionContent') => {
    refetchCollections();

    if (type === 'collection') {
      toggleCollectionShareModal();
    } else if (type === 'collectionContent') {
      toggleCollectionContentShareModal();
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    deselectEntity(id);
  };

  const handleDelete = () => {
    deleteCollection({ id }).then(() => {
      deselectEntity(id);
      setShowDeleteDialog(false);
    });
  };

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const enableDropdownMenu = isAnyPermitted(currentUser.permissions, [
    `collection_entities:edit:${id}`,
    `collections:edit:${id}`,
    `collections:delete:${id}`,
  ]);

  return (
    <>
      {showDeleteDialog && (
        <ConfirmDialog
          show={showDeleteDialog}
          title="Deleting Collection"
          onCancel={cancelDelete}
          onConfirm={handleDelete}>
          <p>You are about to delete the Collection: &quot;{title}&quot;. Are you sure?</p>
        </ConfirmDialog>
      )}

      {showCollectionContentShareModal && (
        <EntityShareModal
          entityId={id}
          entityType="collection_entities"
          entityTypeTitle="Collection Content"
          entityTitle={title}
          description="Search for a User or Team to add as collaborator on this Collection Content."
          onClose={() => handleShareClose('collectionContent')}
        />
      )}

      {showCollectionShareModal && (
        <EntityShareModal
          entityId={id}
          entityType="collection"
          entityTypeTitle="Collection"
          entityTitle={title}
          description="Search for a User or Team to add as collaborator on this Collection."
          onClose={() => handleShareClose('collection')}
        />
      )}

      <ButtonToolbar>
        <ShareButton
          title="Share Content"
          entityId={id}
          entityType="collection_entities"
          onClick={toggleCollectionContentShareModal}
          bsSize="xs"
          bsStyle="primary"
        />
        <ShareButton
          entityId={id}
          entityType="collection"
          onClick={toggleCollectionShareModal}
          bsSize="xs"
          title="Manage"
        />
        <MoreActions disabled={!enableDropdownMenu}>
          <IfPermitted permissions={[`collection_entities:edit:${id}`, `collection:edit:${id}`]} anyPermissions>
            <MenuItem href={Routes.pluginRoute('COLLECTIONS_COLLECTIONID')(id)}>Edit</MenuItem>
          </IfPermitted>
          <IfPermitted permissions={`collections:delete:${id}`}>
            <MenuItem divider />
            <DeleteMenuItem onSelect={onDelete}>Delete</DeleteMenuItem>
          </IfPermitted>
        </MoreActions>
      </ButtonToolbar>
    </>
  );
};

export default CollectionActions;
